import React, { useContext, useMemo, useState } from "react";

import Banner from "../Banner";

import ReleasesContext from "../context";

import { useReleaseUpload } from "frontend/hooks/usePhotoUpload";

import { sortReleasesAlphabetically } from "frontend/components/creator-tools/PhotoUploadPage/helpers";

import { useMutation } from "@apollo/react-hooks";

import { WITHDRAW_PHOTO_RELEASES } from "frontend/components/creator-tools/PhotoUploadPage/PhotoUploadToolbar/queries";
import { determineReleaseErrorMessage } from "frontend/components/creator-tools/PhotoUploadPage/PhotoUpload/Releases/helpers";
import { tooManyReleasesErrorMsg } from "../../Edit/constants";

const MAX_BYTES = 1000000;
const MIN_BYTES = 1;
const PDF_FILE_TYPE = "application/pdf";

export const useReleases = () => {
    const {
        hasReleases,
        setReleases,
        withdrawPhotoReleases,
        releaseUploadErrorMessage,
        setReleaseUploadErrorMessage
    } = useContext(ReleasesContext);

    const { uploadReleases } = useReleaseUpload();

    const handleBannerDismiss = id => {
        withdrawPhotoReleases({
            variables: {
                photoReleaseIds: [id]
            }
        }).then(res => {
            // BE returns the removed release
            const photoReleaseId =
                res.data &&
                res.data.withdrawPhotoReleases &&
                res.data.withdrawPhotoReleases.releases
                    ? res.data.withdrawPhotoReleases.releases[0].photoReleaseId
                    : null;

            const newListOfUploadedReleases = hasReleases.filter(
                release => release.id !== photoReleaseId
            );
            setReleases(newListOfUploadedReleases);
        });
    };
    const handleFileChange = async e => {
        let releaseValidationErrors = [];
        let clearedValidationReleases = [];

        const filesArray = Array.from(e.target.files);

        // Client side validation for file size && file type
        filesArray.forEach(file => {
            const { size, type } = file;

            if (
                size < MIN_BYTES ||
                size > MAX_BYTES ||
                type !== PDF_FILE_TYPE
            ) {
                releaseValidationErrors.push(file);
            } else {
                clearedValidationReleases.push(file);
            }
        });

        const totalFiles = filesArray.length;
        const totalClientErrors = releaseValidationErrors.length;
        const totalClientApprovedFiles = clearedValidationReleases.length;
        const hasFilesToUpload = totalClientApprovedFiles > 0;
        const hasClientSideErrors = totalClientErrors > 0;

        // only client side errors exist
        if (hasClientSideErrors && !hasFilesToUpload) {
            const errorMessage = determineReleaseErrorMessage(
                totalFiles,
                totalClientErrors
            );
            setReleaseUploadErrorMessage(errorMessage);
            return;
        }

        // has files passed client side validation
        if (hasFilesToUpload) {
            const { errors, uploadedReleases } = await uploadReleases(
                clearedValidationReleases
            );

            const hasTooManyReleasesError = Object.values(errors)
                .filter(val => val === tooManyReleasesErrorMsg)
                .includes(tooManyReleasesErrorMsg);

            const backendErrors = Object.keys(errors);
            const totalBackendErrors = backendErrors.length;
            const hasBackendErrors = totalBackendErrors > 0;

            // update error message if any errors exist
            if (hasClientSideErrors || hasBackendErrors) {
                // 100 denied error takes precedent over other errors
                if (hasTooManyReleasesError) {
                    setReleaseUploadErrorMessage(tooManyReleasesErrorMsg);
                } else {
                    const errorMessage = determineReleaseErrorMessage(
                        totalFiles,
                        totalClientErrors + totalBackendErrors
                    );
                    setReleaseUploadErrorMessage(errorMessage);
                }
            }

            setReleases(existingFiles =>
                sortReleasesAlphabetically([
                    ...existingFiles,
                    ...uploadedReleases
                ])
            );
        }
    };

    const releasesBanners =
        hasReleases &&
        hasReleases.length > 0 &&
        hasReleases.map((release, idx) => {
            const { id, name } = release;

            return (
                <Banner
                    key={idx}
                    fileName={name}
                    handleBannerDismiss={() => handleBannerDismiss(id)}
                />
            );
        });

    const ReleasesBanners = useMemo(() => {
        const ReleasesBannersComponent = () => {
            return releasesBanners;
        };

        return ReleasesBannersComponent;
    }, [releasesBanners]);

    const clearOutErrorMessage = () => {
        // Per product, clear out any previous errors on file picker open
        setReleaseUploadErrorMessage("");
    };

    return {
        hasReleases,
        setReleases,
        ReleasesBanners,
        withdrawPhotoReleases,
        releaseUploadErrorMessage,
        setReleaseUploadErrorMessage,
        handleFileChange,
        clearOutErrorMessage
    };
};

export const ReleasesProvider = ({ children }) => {
    const [hasReleases, setReleases] = useState([]);
    const [releaseUploadErrorMessage, setReleaseUploadErrorMessage] = useState(
        ""
    );

    const [withdrawPhotoReleases] = useMutation(WITHDRAW_PHOTO_RELEASES, {
        refetchQueries: ["listReleasesInModerationPhotoBatch"]
    });

    return (
        <ReleasesContext.Provider
            value={{
                hasReleases,
                setReleases,
                withdrawPhotoReleases,
                releaseUploadErrorMessage,
                setReleaseUploadErrorMessage
            }}
        >
            {children}
        </ReleasesContext.Provider>
    );
};
